import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined"
import Chip from "@mui/material/Chip"
import IconButton from "@mui/material/IconButton"
import Link from "@mui/material/Link"
import Tab from "@mui/material/Tab"
import TableCell from "@mui/material/TableCell"
import Tabs from "@mui/material/Tabs"
import { format } from "date-fns"
import { useEffect, useState } from "react"
import { Link as RouterLink, useSearchParams } from "react-router-dom"

import Column from "../../../common/components/Column"
import { useNavigateUpdatedSearchParams } from "../../../common/utils"
import { adminApi } from "../axios"
import AdminHeaderTableCell, {
  AdminSortableHeaderCell,
  AdminTableHeadRow,
  adminTableBodyCellSx,
} from "../components/AdminHeaderTableCell"
import AdminListToolbar, {
  dateRangeToParams,
} from "../components/AdminListToolbar"
import AdminListingTable, {
  AdminTableRow,
} from "../components/AdminListingTable"
import AdminMlsDetailDialog from "../components/AdminMlsDetailDialog"
import AdminMlsPurchaseDetailDialog from "../components/AdminMlsPurchaseDetailDialog"
import AdminPageHeader from "../components/AdminPageHeader"
import AdminTruncatedCell from "../components/AdminTruncatedCell"
import { useAdminPaginatedQuery } from "../hooks/useAdminPaginatedQuery"
import AdminMlsDealCreditsTab from "./AdminMlsDealCreditsTab"
import {
  mlsPurchaseFundingChip,
  mlsPurchaseOrderTypeChip,
} from "./orderTypeChip"

export const MLS_TAB = {
  boards: "boards",
  purchases: "purchases",
  credits: "credits",
}

const MLS_BOARD_FILTER_SPECS = {
  with_users: {
    toParams: (value) => (value === "true" ? { min_users: "1" } : {}),
  },
}

const MLS_BOARD_TOOLBAR_FILTERS = [
  {
    key: "with_users",
    label: "Only boards with active users",
    type: "switch",
    onValue: "true",
    offValue: "",
  },
]

export const MLS_PURCHASE_FILTER_SPECS = {
  status: { param: "status" },
  failed: {
    toParams: (value) => (value === "true" ? { include_failed: "true" } : {}),
  },
  assignment: { param: "assignment_status" },
  // funding=deal: orders that used boards sold on a CRM deal.
  funding: { param: "funding" },
  created: { toParams: dateRangeToParams("created") },
}

const MLS_PURCHASE_TOOLBAR_FILTERS = [
  {
    key: "status",
    label: "Status",
    type: "multi",
    options: [
      { value: "paid", label: "Paid" },
      { value: "awaiting_payment", label: "Awaiting payment" },
      { value: "pending_checkout", label: "Pending checkout" },
      { value: "cancelled", label: "Cancelled" },
      { value: "failed", label: "Failed" },
    ],
  },
  {
    key: "assignment",
    label: "Assignment",
    type: "multi",
    options: [
      { value: "pending", label: "Pending" },
      { value: "assigned", label: "Assigned" },
      { value: "failed", label: "Failed" },
    ],
  },
  {
    key: "funding",
    label: "Paid by",
    type: "select",
    anyLabel: "All",
    options: [
      { value: "deal", label: "Deal sale" },
      { value: "paid", label: "Paid checkout" },
    ],
  },
  { key: "created", label: "Created", type: "daterange" },
  {
    key: "failed",
    label: "Show failed",
    type: "switch",
    onValue: "true",
    offValue: "",
    chip: true,
  },
]

function formatWhen(value) {
  if (!value) return "—"
  try {
    return format(new Date(value), "MMM d, yyyy")
  } catch {
    return "—"
  }
}

function AdminMlsTab() {
  const [detailMlsId, setDetailMlsId] = useState(null)

  const query = useAdminPaginatedQuery(adminApi.fetchMlsList, {
    filters: MLS_BOARD_FILTER_SPECS,
    defaultSortColumn: "name",
  })
  const {
    rows,
    isLoading,
    pageIndex,
    setPageIndex,
    pageSize,
    setPageSize,
    totalResultsSize,
    refresh,
  } = query

  return (
    <Column gap={2}>
      <AdminListToolbar
        query={query}
        searchLabel="Search MLS name"
        noun={totalResultsSize === 1 ? "board" : "boards"}
        filters={MLS_BOARD_TOOLBAR_FILTERS}
      />
      <AdminListingTable
        rows={rows}
        isLoading={isLoading}
        pageIndex={pageIndex}
        setPageIndex={setPageIndex}
        pageSize={pageSize}
        setPageSize={setPageSize}
        totalResultsSize={totalResultsSize}
        columnCount={4}
        tableHead={
          <AdminTableHeadRow>
            <AdminSortableHeaderCell
              columnName="name"
              label="MLS Name"
              query={query}
              minWidth={{ xs: 160, sm: 240 }}
            />
            <AdminHeaderTableCell minWidth={{ xs: 80, sm: 120 }}>
              MLS ID
            </AdminHeaderTableCell>
            <AdminSortableHeaderCell
              columnName="user_count"
              label="Active users"
              query={query}
              minWidth={{ xs: 80, sm: 120 }}
            />
            <AdminHeaderTableCell align="right" minWidth={{ xs: 64, sm: 80 }}>
              Details
            </AdminHeaderTableCell>
          </AdminTableHeadRow>
        }
      >
        {rows.map((mls) => (
          <AdminTableRow key={mls.id} hover>
            <TableCell sx={{ ...adminTableBodyCellSx, fontWeight: 500 }}>
              {mls.name}
            </TableCell>
            <AdminTruncatedCell value={mls.id} />
            <TableCell sx={adminTableBodyCellSx}>
              {mls.user_count ? (
                <Link
                  component={RouterLink}
                  to={`/admin/users?mls=${encodeURIComponent(mls.id)}`}
                  title="Active users on this board"
                >
                  {mls.user_count}
                </Link>
              ) : (
                0
              )}
            </TableCell>
            <TableCell align="right" sx={adminTableBodyCellSx}>
              <IconButton
                size="small"
                aria-label="View MLS details"
                onClick={() => setDetailMlsId(mls.id)}
              >
                <VisibilityOutlinedIcon fontSize="small" />
              </IconButton>
            </TableCell>
          </AdminTableRow>
        ))}
      </AdminListingTable>
      <AdminMlsDetailDialog
        open={Boolean(detailMlsId)}
        mlsId={detailMlsId}
        onClose={() => {
          setDetailMlsId(null)
          refresh()
        }}
      />
    </Column>
  )
}

function AdminMlsPurchaseTab() {
  const [searchParams] = useSearchParams()
  const navigateUpdatedSearchParams = useNavigateUpdatedSearchParams()
  const [detailId, setDetailId] = useState(null)

  // Depend on the purchase_id value, not the whole searchParams object:
  // pagination, sort and search all rewrite the query string while preserving
  // purchase_id, which re-opened a dialog the admin had just closed.
  const deepLinkedPurchaseId = (searchParams.get("purchase_id") || "").trim()
  useEffect(() => {
    if (deepLinkedPurchaseId) {
      setDetailId(deepLinkedPurchaseId)
    }
  }, [deepLinkedPurchaseId])

  const query = useAdminPaginatedQuery(adminApi.fetchMlsPurchases, {
    filters: MLS_PURCHASE_FILTER_SPECS,
    defaultSortColumn: "created",
    defaultSortDirection: "desc",
  })
  const {
    rows,
    isLoading,
    pageIndex,
    setPageIndex,
    pageSize,
    setPageSize,
    totalResultsSize,
  } = query

  function closeDetail() {
    setDetailId(null)
    // Drop the deep link so a reload doesn't reopen the dialog; pin the tab,
    // which purchase_id was implying.
    if (deepLinkedPurchaseId) {
      navigateUpdatedSearchParams({
        purchase_id: null,
        tab: MLS_TAB.purchases,
      })
    }
  }

  return (
    <Column gap={2}>
      <AdminListToolbar
        query={query}
        searchLabel="Search customer, purchase ID, Zoho subscription or invoice"
        noun={totalResultsSize === 1 ? "order" : "orders"}
        filters={MLS_PURCHASE_TOOLBAR_FILTERS}
      />
      <AdminListingTable
        rows={rows}
        isLoading={isLoading}
        pageIndex={pageIndex}
        setPageIndex={setPageIndex}
        pageSize={pageSize}
        setPageSize={setPageSize}
        totalResultsSize={totalResultsSize}
        columnCount={11}
        tableHead={
          <AdminTableHeadRow>
            <AdminSortableHeaderCell
              columnName="user__email"
              label="Customer"
              query={query}
              minWidth={{ xs: 120, sm: 180 }}
            />
            <AdminHeaderTableCell minWidth={{ xs: 88, sm: 120 }}>
              Type
            </AdminHeaderTableCell>
            <AdminHeaderTableCell minWidth={{ xs: 88, sm: 130 }}>
              Paid by
            </AdminHeaderTableCell>
            <AdminHeaderTableCell minWidth={{ xs: 120, sm: 200 }}>
              MLS
            </AdminHeaderTableCell>
            <AdminSortableHeaderCell
              columnName="status"
              label="Status"
              query={query}
              minWidth={{ xs: 72, sm: 100 }}
            />
            <AdminSortableHeaderCell
              columnName="assignment_status"
              label="Assignment"
              query={query}
              minWidth={{ xs: 72, sm: 100 }}
            />
            <AdminSortableHeaderCell
              columnName="created"
              label="Created"
              query={query}
              minWidth={{ xs: 72, sm: 100 }}
            />
            <AdminSortableHeaderCell
              columnName="paid_at"
              label="Paid"
              query={query}
              minWidth={{ xs: 72, sm: 100 }}
            />
            <AdminSortableHeaderCell
              columnName="zoho_subscription_id"
              label="Zoho Subscription"
              query={query}
              minWidth={{ xs: 100, sm: 160 }}
            />
            <AdminSortableHeaderCell
              columnName="zoho_invoice_id"
              label="Invoice ID"
              query={query}
              minWidth={{ xs: 80, sm: 140 }}
            />
            <AdminHeaderTableCell align="right" minWidth={{ xs: 64, sm: 80 }}>
              View
            </AdminHeaderTableCell>
          </AdminTableHeadRow>
        }
      >
        {rows.map((row) => {
          const typeChip = mlsPurchaseOrderTypeChip(row)
          const fundingChip = mlsPurchaseFundingChip(row)
          return (
            <AdminTableRow key={row.id} hover>
              <AdminTruncatedCell value={row.user_email} />
              <TableCell sx={adminTableBodyCellSx}>
                <Chip
                  size="small"
                  label={typeChip.label}
                  color={typeChip.color}
                  variant={typeChip.variant}
                />
              </TableCell>
              <TableCell sx={adminTableBodyCellSx}>
                <Chip
                  size="small"
                  label={fundingChip.label}
                  color={fundingChip.color}
                  variant={fundingChip.variant}
                />
              </TableCell>
              <AdminTruncatedCell value={(row.mls_summary || []).join(", ")} />
              <TableCell sx={adminTableBodyCellSx}>
                <Column gap={0.5} sx={{ alignItems: "flex-start" }}>
                  <span>{row.status}</span>
                  {row.subscription_cancel_at_end && (
                    <Chip
                      size="small"
                      label={
                        row.subscription_term_ends_at
                          ? `Cancels ${formatWhen(
                              row.subscription_term_ends_at
                            )}`
                          : "Cancels at period end"
                      }
                      color="warning"
                      variant="outlined"
                    />
                  )}
                </Column>
              </TableCell>
              <AdminTruncatedCell value={row.assignment_status} />
              <AdminTruncatedCell value={formatWhen(row.created)} />
              <AdminTruncatedCell value={formatWhen(row.paid_at)} />
              <AdminTruncatedCell
                value={
                  [
                    row.zoho_subscription_id,
                    row.deal_subscription_id
                      ? `deal ${row.deal_subscription_id}`
                      : null,
                  ]
                    .filter(Boolean)
                    .join(" · ") || "—"
                }
              />
              <AdminTruncatedCell value={row.zoho_invoice_id || "—"} />
              <TableCell align="right" sx={adminTableBodyCellSx}>
                <IconButton
                  size="small"
                  aria-label="View purchase details"
                  onClick={() => setDetailId(row.id)}
                >
                  <VisibilityOutlinedIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </AdminTableRow>
          )
        })}
      </AdminListingTable>
      <AdminMlsPurchaseDetailDialog
        open={Boolean(detailId)}
        purchaseId={detailId}
        onClose={closeDetail}
      />
    </Column>
  )
}

/** Tab comes from the URL; a purchase deep link always opens Purchases. */
export function resolveMlsTab(searchParams) {
  const tab = searchParams.get("tab")
  if (Object.values(MLS_TAB).includes(tab)) return tab
  return searchParams.get("purchase_id") ? MLS_TAB.purchases : MLS_TAB.boards
}

// Per-tab URL state that must not leak into the other tab.
const TAB_SCOPED_PARAMS = [
  "q",
  "pi",
  "sc",
  "sd",
  "purchase_id",
  ...Object.keys(MLS_BOARD_FILTER_SPECS),
  ...Object.keys(MLS_PURCHASE_FILTER_SPECS),
]

export default function AdminMlsManagement() {
  const [searchParams] = useSearchParams()
  const navigateUpdatedSearchParams = useNavigateUpdatedSearchParams()
  const tab = resolveMlsTab(searchParams)

  function setTab(nextTab) {
    if (nextTab === tab) return
    const cleared = Object.fromEntries(
      TAB_SCOPED_PARAMS.map((param) => [param, null])
    )
    navigateUpdatedSearchParams({ ...cleared, tab: nextTab })
  }

  return (
    <Column gap={2}>
      <AdminPageHeader
        title="MLS management"
        subtitle="Manage active MLSs and review MLS purchase orders."
      />
      <Tabs
        value={tab}
        onChange={(_, newValue) => setTab(newValue)}
        sx={{ borderBottom: 1, borderColor: "divider", mb: 1 }}
      >
        <Tab label="MLS" value={MLS_TAB.boards} />
        <Tab label="MLS Purchase" value={MLS_TAB.purchases} />
        <Tab label="Deal credits" value={MLS_TAB.credits} />
      </Tabs>
      {tab === MLS_TAB.boards && <AdminMlsTab key="boards" />}
      {tab === MLS_TAB.purchases && <AdminMlsPurchaseTab key="purchases" />}
      {tab === MLS_TAB.credits && <AdminMlsDealCreditsTab key="credits" />}
    </Column>
  )
}
